import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import List from 'components/AssetList';
import Header from 'components/AssetList/Header';
import { useToggle } from 'components/AssetList/hooks';
import LayoutListItem from 'components/LayoutListItem';
import { EntityType } from 'const';
import { toggleUpdatedDocumentsDisplaying as toggleUpdatedDocumentsDisplayingAction } from 'containers/App/actions';
import * as AppModels from 'containers/App/models';
import { showUpdatedReusableLayouts as showUpdatedReusableLayoutsSelector } from 'containers/App/selectors';
import { CombinedLayoutDocumentsMap, ReusableLayoutMap, ScreenMap } from 'models';
import { intlGet } from 'utils/intlGet';
import styles from './styles.module.scss';

type Props = {
  isExpand?: boolean;
  reusableLayouts: CombinedLayoutDocumentsMap;
  storyCardId?: string;
  toggleExpand?(): void;
  updatedInPromoMats?: boolean;
  isReadOnly?: boolean;
  screen?: ScreenMap;
  dataTestId?: string;
};

const LayoutList: React.FunctionComponent<Props> = (props) => {
  const {
    isExpand,
    reusableLayouts,
    storyCardId,
    toggleExpand,
    updatedInPromoMats,
    isReadOnly,
    screen,
    dataTestId = 'reusableLayoutList',
  } = props;
  const dispatch = useDispatch();
  const showUpdatedReusableLayouts = useSelector(showUpdatedReusableLayoutsSelector);
  const toggleUpdatedDocumentsDisplaying: AppModels.ActionCreator.ToggleUpdatedDocumentsDisplaying = (...args) => {
    return dispatch(toggleUpdatedDocumentsDisplayingAction(...args));
  };
  const {
    expanded,
    toggle,
  } = useToggle({
    toggleUpdatedDocumentsDisplaying,
    showUpdatedDocuments: showUpdatedReusableLayouts,
    entityType: EntityType.LAYOUT,
    toggle: toggleExpand,
    isExpand,
  });

  const renderHint = (): JSX.Element => (
    <div className={styles.hint}>
      <div>{intlGet('Assets', 'ReusableLayouts.Hint.Title')}</div>
      <div>{intlGet('Assets', 'ReusableLayouts.Hint.Message')}</div>
    </div>
  );

  return (
    <div className={styles.ReusableLayoutList} data-title="reusable-layouts" data-testid={dataTestId}>
      {
        !storyCardId &&
        <Header
          title={intlGet('Assets', 'ReusableLayouts', { count: reusableLayouts.size })}
          showUpdatesHint={updatedInPromoMats && showUpdatedReusableLayouts}
          onClick={toggle}
          expanded={expanded}
          dataTestId={`${dataTestId}-header`}
        />
      }
      {
        (expanded || storyCardId) &&
          <List
            border={!storyCardId}
            elements={reusableLayouts}
            subheader={!storyCardId && reusableLayouts && reusableLayouts.size === 0 && renderHint()}
            renderElement={(reusableLayout: ReusableLayoutMap, index: number): React.ReactNode => (
              <LayoutListItem
                key={reusableLayout.get('id')}
                document={reusableLayout}
                screen={screen}
                isReadOnly={isReadOnly}
                storyCardId={storyCardId}
                dataTestId={`${dataTestId}-list-item${index}`}
              />
            )}
            dataTestId={`${dataTestId}-list`}
          />
      }
    </div >
  );
};

export default LayoutList;
